import {Lead} from '../models/lead.model';
import {REPORTING_IDENTIFIER} from '../models/snippet-payload.model';
import {CONVERSION_TYPE} from '../models/conversion-type.enum';

type Message = {
	isFullpathGpt?: boolean,
	action: string,
	payload?: {
		height?: string;
		status?: string;
		viewMode?: string;
		success_data?: { // set to mirror Connect payload structure for reporting consistency
			lead?: Lead,
			chatConversionType?: CONVERSION_TYPE;
		};
		label?: string;
        url? :string
		elementText?: string;
	}
};

export enum INCOMING_ACTION {
	TRACK_DATA = 'track_data',
	SHOW_PICKUP_LINE = 'show_pickup_line',
	SHOW_HOME = 'show_home',
}

export enum OUTGOING_ACTION {
	HIDE = 'hide',
	SHOW = 'show',
	RESIZE = 'resize',
	FPC_READY = 'fpc_ready',
	LOG_CHAT_EVENT = 'log_chat_event',
	VIEW_MODE_UPDATE = 'view_mode_update',
	CONVERSION = 'conversion',
	PICKUP_LINE_DISMISSED = 'pickup_line_dismissed',
	OPEN_IN_SAME_TAB = 'open_in_same_tab',
	SERVICE_APPOINTMENT_SCHEDULED = 'service_appointment_scheduled',
	FP_CTA_CLICK = 'fp_cta_click',
}

type PostMessageCallback = (message: unknown) => void;

class SnippetService {
	private listeners: Map<string, PostMessageCallback[]> = new Map();
	private sentFpcReady = false;

	public sendMessage(message: Message, targetOrigin = "*") {
		if (message.action === OUTGOING_ACTION.FPC_READY) {
			if (this.sentFpcReady) {
				return;
			}
			this.sentFpcReady = true;
		}
		message.isFullpathGpt = true;
		window.parent.postMessage(message, targetOrigin);
	}

	public subscribe(action: string, callback: PostMessageCallback) {
		if (!this.listeners.has(action)) {
			this.listeners.set(action, []);
		} else {
			return;
		}
		const eventListeners = this.listeners.get(action) || [];
		eventListeners.push(callback);

		const eventListener = (event: MessageEvent) => {
			if (event.data.action === action) {
				eventListeners.forEach((callback) => callback(event.data));
			}
		};
		window.addEventListener('message', eventListener);

		return () => {
			const index = eventListeners.indexOf(callback);
			if (index !== -1) {
				eventListeners.splice(index, 1);
			}
			window.removeEventListener('message', eventListener);
		};
	}

	public sendConversionMessage(lead: Lead, type: CONVERSION_TYPE = CONVERSION_TYPE.CONVERSATIONAL): void {
		this.sendMessage({
			action: OUTGOING_ACTION.CONVERSION,
			payload: {
				success_data: {
					lead,
					chatConversionType: type,
				},
				label: REPORTING_IDENTIFIER,
			},
		});
	}

	public sendServiceAppointmentScheduledMessage(): void {
		this.sendMessage({
			action: OUTGOING_ACTION.SERVICE_APPOINTMENT_SCHEDULED,
			payload: {
				label: REPORTING_IDENTIFIER,
			},
		});
	}

	public sendCtaClickMessage(elementText: string): void {
		this.sendMessage({
			action: OUTGOING_ACTION.FP_CTA_CLICK,
			payload: {
				elementText,
			},
		});
	}
}

export const snippetService = new SnippetService();
